import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Select, Table, UserSelect } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'

import { useQuery } from '@apollo/client'
import { subWeeks } from 'date-fns'
import type {
  AllUserDocumentCategoriesQuery,
  AllUserDocumentsQuery,
  AllUserDocumentsQueryVariables,
  ShallowUserNode,
} from '@/modules/users'
import {
  ALL_USER_DOCUMENTS_QUERY,
  ALL_USER_DOCUMENT_CATEGORIES_QUERY,
} from '@/modules/users/queries'
import React, { useEffect, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { format } from '@/utils/date-fns'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type {
  EditUserDocumentPromptResolve,
  UserDocumentCategoriesSettingsForm,
  UserDocumentCategoryForSettings,
  UserDocumentForSettings,
} from '../components'
import { EditUserDocumentPrompt } from '../components'
import {
  OldAddButton,
  InfoBox,
  OldSection,
  SectionHeader,
  SettingsWrapper,
} from '../components/common'
import type { UserDocumentCategoryForm } from '../components/UserDocumentSettings/UserDocumentCategoryPrompt'
import { UserDocumentCategoryPrompt } from '../components/UserDocumentSettings/UserDocumentCategoryPrompt'
import { UserDocumentCategorySettingsCard } from '../components/UserDocumentSettings/UserDocumentCategorySettingsCard'
import { UserDocumentCategorySettingsTable } from '../components/UserDocumentSettings/UserDocumentCategorySettingsTable'
import {
  useUserDocumentCategoryMutations,
  useUserDocumentMutations,
} from '../util/mutations.hooks'

const Header = styled.header`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 1rem;

  h2 {
    margin: 0 1rem 0 0;
  }

  ${props => props.theme.media.mobile} {
    display: grid;
    grid-template-rows: auto auto;
    gap: 0.5rem;

    margin-bottom: 0;
  }
`

const SelectFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const TableRow = styled.tr<{ expired?: boolean }>`
  background: ${props => (props.expired ? props.theme.colors.red12 : 'none')};
`

interface UserDocumentSettingsProps {}

export const UserDocumentSettings: React.FC<UserDocumentSettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.user-documents-1',
    info2: 'settings.info.user-documents-2',

    selectCategory: 'settings.select-category',

    documents: 'common.documents',
    noDocuments: 'documents.no-documents',

    name: 'common.name',
    responsible: 'common.responsible',
    user: 'common.user',
    notificationTime: 'documents.notification-time-alt',
    expirationDate: 'documents.expiration-date',

    prompt: {
      delete: ['settings.prompts.user-document-delete', { name: '' }],
      deleteTitle: 'settings.prompts.user-document-delete-title',
    },
    validation: {
      nameExists: 'settings.validation.user-document-name-exists',
    },
    userDocumentCategories: 'common.user-document-categories',
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const mutations = useUserDocumentMutations()
  const categoryMutations = useUserDocumentCategoryMutations()

  const [user, setUser] = useState<ShallowUserNode | null>(null)
  const [category, setCategory] =
    useState<UserDocumentCategoryForSettings | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const addToast = useToast()
  const categoriesFilter = category ? [category.id] : null

  const { data: queryData, loading: queryLoading } = useQuery<
    AllUserDocumentsQuery,
    AllUserDocumentsQueryVariables
  >(ALL_USER_DOCUMENTS_QUERY, {
    variables: {
      user: user?.id,
      orderBy: 'expirationDate',
      categories: categoriesFilter,
    },
    onError: onErrorAuto(),
  })

  const { data: categoryData } = useQuery<AllUserDocumentCategoriesQuery>(
    ALL_USER_DOCUMENT_CATEGORIES_QUERY,
    {
      onError: onErrorAuto(),
    }
  )

  const editableCategories =
    categoryData?.allUserDocumentCategories.edges.map(edge => edge.node) ?? []

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<UserDocumentCategoriesSettingsForm>({
    values: {
      newName: '',
      editName: '',
    },
  })

  const documents =
    queryData?.allUserDocuments.edges.map(edge => edge.node) ?? []

  async function handleEdit(document: UserDocumentForSettings) {
    const { data } = await addPrompt<EditUserDocumentPromptResolve | null>(
      resolve => (
        <EditUserDocumentPrompt userDocument={document} onSubmit={resolve} />
      )
    )
    if (data === null) return
    const { form, hasNotification } = data

    const expirationDate = !hasNotification ? null : form.expirationDate
    const notificationTime = !hasNotification
      ? null
      : !expirationDate || !form.notificationTime
      ? null
      : subWeeks(expirationDate, form.notificationTime)

    void mutations.patch({
      variables: {
        id: document.id,
        input: {
          name: form.name,
          responsibleUser: !hasNotification
            ? null
            : form.responsibleUser?.id ?? null,
          expirationDate,
          notificationTime,
          categories: form.categories,
        },
      },
    })
  }
  async function handleDelete(document: UserDocumentForSettings) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: document.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: { id: document.id },
    })
  }

  const isLoading = queryLoading || mutations.loading

  async function submitNewUserDocumentCategory() {
    if (!validateName(form.newName)) return

    await categoryMutations.create({
      variables: {
        input: {
          name: form.newName,
        },
      },
    })
    setAdding(false)
  }

  async function handleCreatePrompt() {
    const { data } = await addPrompt<UserDocumentCategoryForm | null>(
      resolve => (
        <UserDocumentCategoryPrompt
          editableCategories={editableCategories}
          onSubmit={resolve}
        />
      )
    )

    if (data === null) return

    void categoryMutations.create({
      variables: {
        input: { ...data },
      },
    })
  }

  async function handleEditPrompt(category: UserDocumentCategoryForSettings) {
    const initialData: UserDocumentCategoryForm = {
      name: category.name,
    }
    const { data } = await addPrompt<UserDocumentCategoryForm | null>(
      resolve => (
        <UserDocumentCategoryPrompt
          initialData={initialData}
          editableCategories={editableCategories}
          onSubmit={resolve}
        />
      )
    )
    if (data === null) return

    void categoryMutations.patch({
      variables: {
        id: category.id,
        input: { ...data },
      },
    })
  }
  function validateName(name: string) {
    if (name === '') return false
    if (editableCategories.find(type => type.name === form.newName)) {
      addToast('warning', translations.validation.nameExists)
      return false
    }
    return true
  }

  function checkNameHasChanged(id: string) {
    const category = editableCategories.find(type => type.id === id)
    if (typeof category === 'undefined') return true

    return category.name !== form.editName
  }

  async function submitEditUserDocumentCategoryName() {
    if (!validateName(form.editName) || editing === null) return
    if (!checkNameHasChanged(editing)) {
      setEditing(null)
      return
    }

    await categoryMutations.patch({
      variables: {
        id: editing,
        input: { name: form.editName },
      },
    })
    setEditing(null)
  }

  async function handleDeletePrompt(category: UserDocumentCategoryForSettings) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: category.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void categoryMutations.delete({
      variables: {
        id: category.id,
      },
    })
  }

  useEffect(() => {
    if (!adding && form.newName !== '') {
      updateForm({
        newName: '',
      })
    }
  }, [adding, form.newName, updateForm])

  useEffect(() => {
    if (editing === null && form.editName !== '') {
      updateForm({ editName: '' })
    }
  }, [editing, form.editName, updateForm])

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>

      <OldSection>
        <Header>
          <SectionHeader loading={queryLoading}>
            {translations.userDocumentCategories}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton adding={adding} onClick={handleCreatePrompt} />
          )}
        </Header>
        {isMobileOnly ? (
          <UserDocumentCategorySettingsCard
            editableCategories={editableCategories}
            form={form}
            adding={adding}
            editing={editing}
            onFormChange={handler}
            onUpdateForm={updateForm}
            onAdd={setAdding}
            onAddSubmit={submitNewUserDocumentCategory}
            onEdit={setEditing}
            onEditSubmit={submitEditUserDocumentCategoryName}
            onDelete={handleDeletePrompt}
          />
        ) : (
          <UserDocumentCategorySettingsTable
            editableCategories={editableCategories}
            onEditClick={handleEditPrompt}
            onDelete={handleDeletePrompt}
          />
        )}
      </OldSection>
      <OldSection>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.documents}
            {!isMobileOnly ? ':' : ''}
          </SectionHeader>
          <SelectFilter>
            <Select
              value={category?.id ?? null}
              fullWidth={isMobileOnly}
              onChange={(_, category) => setCategory(category?.extra ?? null)}
              options={editableCategories.map(category => ({
                value: category.id,
                label: category.name,
                extra: category,
              }))}
              placeholder={translations.selectCategory}
              searchable
              nullable
            />

            <UserSelect
              value={user?.id ?? null}
              fullWidth={isMobileOnly}
              onChange={(_, user) => setUser(user?.extra ?? null)}
              nullable
            />
          </SelectFilter>
        </Header>

        <Table
          noData={!isLoading && documents.length === 0}
          noDataText={translations.noDocuments}
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.name}</th>
              <th>{translations.user}</th>

              {!isMobileOnly && (
                <>
                  <th>{translations.responsible}</th>
                  <th>{translations.expirationDate}</th>
                  <th>{translations.notificationTime}</th>
                </>
              )}

              <th></th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {documents.map(document => {
              const expirationDate = document.expirationDate
                ? new Date(document.expirationDate)
                : null
              const expired = expirationDate
                ? expirationDate < new Date()
                : false
              const notificationTime = document.notificationTime
                ? format(new Date(document.notificationTime), 'PPP HH:mm')
                : '-'

              const expirationDateString = expirationDate
                ? format(expirationDate, 'PPP')
                : '-'

              return (
                <TableRow key={document.id} expired={expired}>
                  <td>{document.name}</td>

                  {!isMobileOnly && (
                    <>
                      <td>
                        {!document.user ? (
                          '-'
                        ) : (
                          <PlainLink
                            to={`/users/${document.user.id}`}
                            $hoverUnderline
                            $hoverColor="secondary"
                          >
                            {document.user.fullName}
                          </PlainLink>
                        )}
                      </td>
                      <td>
                        {!document.responsibleUser ? (
                          '-'
                        ) : (
                          <PlainLink
                            to={`/users/${document.responsibleUser.id}`}
                            $hoverUnderline
                            $hoverColor="secondary"
                          >
                            {document.responsibleUser.fullName}
                          </PlainLink>
                        )}
                      </td>
                      <td>{expirationDateString}</td>
                      <td>{notificationTime}</td>
                    </>
                  )}

                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(document)}
                    />
                  </td>

                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(document)}
                    />
                  </td>
                </TableRow>
              )
            })}
          </tbody>
        </Table>
      </OldSection>
    </SettingsWrapper>
  )
}
